// ─────────────────────────────────────────────────────────────────
// TypeScript interfaces for backend DTOs used by CourseGroupControllerV2
// Source: Capeesh.Domain.Model.CourseGroups + related namespaces
// ─────────────────────────────────────────────────────────────────

// ─── Enums ────────────────────────────────────────────────────────

export enum CourseGroupType {
    NotDefined = 0,
    BabbelEnglishProgressives = 1,
    BabbelGermanProgressives = 2,
    BabbelPolishProgressives = 3,
    CapeeshNorwegianProgressives = 4,
    DirectUserGroupAccessFallback = 5,
    CapeeshNorwegianProgressivesFree = 6,
    CapeeshNyNorskProgressive = 7,
}

export enum CourseRibbonProgressionType {
    NotDefined = 0,
    AllLessonsCompleted = 1,
    AllLessonsPerfectlyCompleted = 2,
}

export enum TopicLessonType {
    NotSet = 0,
    Vocabulary = 1,
    Quiz = 2,
    Scenario = 3,
}

export enum LessonContentType {
    NotDefined = 0,
    Roleplay = 1,
    Vocabulary = 2,
    Compliance = 4,
    Exam = 5,
}

export enum LessonSegmentationType {
    FallbackNotSet = 0,
    IntroLesson = 1,
    RepeatLesson = 2,
    Finals = 3,
    TopOfLesson = 4,
}

export enum MentorEnum {
    NoMentor = 100,
    Abe = 0,
    Lin = 1,
    Bell = 2,
    Ron = 3,
    Solina = 4,
}

export enum CompletionState {
    FallbackNotSet = 0,
    Completed = 1,
    Exited = 2,
    Crashed = 3,
    Inactivity = 4,
    NoMicrophonePermission = 5,
    FailedAssetRetrieval = 6,
    MicrosoftSpeechFailed = 7,
    CompletedWithSkip = 8,
}

// ─── Course Group Overview (V2) ───────────────────────────────────
// GET api/course/group/v2

export interface CourseGroupSupportedLanguageDto {
    l1Id: number
    l1NativeFriendlyName: string
}

export interface CourseGroupMetadataV2Dto {
    courseGroupId: string
    index: number
    l1Id: number | null
    l2Only: boolean
    nativeSpeakerEnabled: boolean
    supportedL1Ids: number[]
    supportedL1Languages: CourseGroupSupportedLanguageDto[]
}

export interface CourseGroupOverviewV2Dto {
    courseGroupIds: string[]
    courseGroupMetadataV2Dtos: CourseGroupMetadataV2Dto[]
}

// ─── Course Group Detail (V1) ─────────────────────────────────────
// GET api/course/group/v2/{courseGroupId}/l1/{l1Id}

export interface CourseMetadataV1Dto {
    topicId: number
    l2TopicName: string
    l2TopicNameLanguageId: number
    l1TopicName: string
    l1TopicNameLanguageId: number
    l2TopicDescription: string
    l2TopicDescriptionLanguageId: number
    l1TopicDescription: string
    l1TopicDescriptionLanguageId: number
    imageUrl: string
}

export interface CourseGroupSubPartDto {
    id: string | null
    l2Name: string
    l2NameLanguageId: number
    l2Description: string
    l2DescriptionLanguageId: number
    l1Name: string
    l1NameLanguageId: number
    l1Description: string
    l1DescriptionLanguageId: number
    index: number
    courses: CourseMetadataV1Dto[]
}

export interface CourseGroupV1Dto {
    id: string
    generationIndex: number
    l2Name: string
    l2NameLanguageId: number
    l2Description: string
    l2DescriptionLanguageId: number
    l1Name: string
    l1NameLanguageId: number
    l1Description: string
    l1DescriptionLanguageId: number
    courseGroupType: CourseGroupType
    l2Id: number
    l1Id: number
    imageUrl: string
    courseGroupSubParts: CourseGroupSubPartDto[]
    l1LanguageIds: number[]
    modifiedAt: string
}

// ─── Course Group Progression ─────────────────────────────────────
// GET api/course/group/v2/{courseGroupId}/l1/{l1Id}/progression

export interface CourseProgressionRibbonDto {
    id: string | null
    courseRibbonProgressionType: CourseRibbonProgressionType
    ribbonCompletedDate: string
}

export interface CourseProgressionDto {
    topicId: number
    courseXp: number
    starsAttained: number
    starsAttainableInCourse: number
    courseNormalizedProgression: number
    courseProgressionRibbons: CourseProgressionRibbonDto[]
}

export interface CourseGroupSubPartProgressionDto {
    id: string
    courseProgressions: CourseProgressionDto[]
}

export interface CourseGroupNextLessonV1Dto {
    courseGroupId: string
    courseGroupSubPartId: string
    topicId: number
    exerciseId: number | null
    customLessonId: string | null
    l2Name: string
    l2NameLanguageId: number
    l1Name: string
    l1NameLanguageId: number
    topicLessonType: TopicLessonType
}

export interface CourseGroupProgressionV1Dto {
    id: string
    generationIndex: number
    courseGroupGenerationIndex: number
    l1Id: number | null
    l2Id: number
    courseGroupXp: number
    starsAttained: number
    starsAttainableInCourseGroup: number
    modifiedAt: string
    courseGroupNormalizedProgression: number
    courseGroupSubPartProgressions: CourseGroupSubPartProgressionDto[]
    courseRibbonProgressionType: CourseRibbonProgressionType
    courseGroupNextLesson: CourseGroupNextLessonV1Dto | null
}

// ─── Course Lesson Order ──────────────────────────────────────────
// GET api/course/group/v2/{courseGroupId}/l1/{l1Id}/course/{topicId}/lesson/order

export interface CourseLessonOrderItemDto {
    tempId: string
    index: number
    exerciseId: number | null
    customLessonId: string | null
    name: string
    nameLanguageId: number
    description: string
    descriptionLanguageId: number
    topicLessonType: TopicLessonType
    topicLessonTypeName: string
    lessonContentType: LessonContentType
    lessonSegmentationType: LessonSegmentationType
    lessonSegmentationTypeName: string
    mentor: MentorEnum
    gateable: boolean
    imageUrl: string
}

export interface CourseLessonOrderDto {
    topicId: number
    courseLessonOrderItems: CourseLessonOrderItemDto[]
    modifiedAt: string
}

// ─── Course Lesson Order Progression ──────────────────────────────
// GET api/course/group/v2/{courseGroupId}/l1/{l1Id}/course/{topicId}/lesson/order/progression

export interface CourseLessonOrderItemProgressionDto {
    customLessonId: string | null
    exerciseId: number | null
    lessonXp: number
    passed: boolean
    stars: number
    normalizedHighScore: number
}

export interface CourseLessonOrderProgressionDto {
    topicId: number
    starsAttained: number
    starsAttainableInCourse: number
    courseNormalizedProgression: number
    courseProgressionRibbons: CourseProgressionRibbonDto[]
    courseLessonOrderItemProgressions: CourseLessonOrderItemProgressionDto[]
}

// ─── Set L1 Request ───────────────────────────────────────────────
// POST api/course/group/v2/{courseGroupId}/l1

export interface CourseGroupLanguageRequestDto {
    courseGroupId: string
    l1Id: number
}

// ─── Scenario Report (V3) ────────────────────────────────────────
// POST api/course/group/v2/.../scenario/v3/

export interface ScenarioReportV3Dto {
    customLessonId: string
    generationIndex: number
    triedAgain: boolean
    timeToCompleteInSec: number
    completionState: CompletionState
    loadingTime: number
    orderedStageResults: any[]
    lessonResult: any
    migratedBecauseOfWrongScoringInApp: boolean
}

export interface ScenarioReportV3ResultDto {
    customLessonId: string
    userLevelBefore: number
    userLevelAfter: number
}

// ─── Reducer State Shape ──────────────────────────────────────────

export interface StudentCourseState {
    fetchingCourseGroupOverview: boolean
    courseGroupOverview: CourseGroupOverviewV2Dto | null

    courseGroupDetailsMap: Record<string, CourseGroupV1Dto>
    fetchingCourseGroupDetailsMap: Record<string, boolean>

    fetchingCourseGroup: boolean
    currentCourseGroup: CourseGroupV1Dto | null
    currentCourseGroupId: string | null
    currentL1Id: number | null

    fetchingCourseGroupProgression: boolean
    courseGroupProgression: CourseGroupProgressionV1Dto | null

    courseGroupProgressionMap: Record<string, CourseGroupProgressionV1Dto>
    fetchingCourseGroupProgressionMap: Record<string, boolean>

    settingL1: boolean
    courseGroupMetadata: CourseGroupMetadataV2Dto | null

    fetchingCourseLessonOrder: boolean
    courseLessonOrder: CourseLessonOrderDto | null
    currentTopicId: number | null

    fetchingCourseLessonOrderProgression: boolean
    courseLessonOrderProgression: CourseLessonOrderProgressionDto | null

    fetchingLesson: boolean
    currentLesson: any | null
    currentCustomLessonId: string | null

    reportingLesson: boolean
    lessonReportResult: ScenarioReportV3ResultDto | null

    imageCache: Record<string, string>
    fetchingImages: Record<string, boolean>

    audioCache: Record<string, string>
    fetchingAudio: Record<string, boolean>

    // Main menu selection state (persisted across navigation)
    selectedCourseGroupId: string | null
    selectedTopicId: number | null
}
